import { Users, Play } from 'lucide-react';
import type { Product } from './types';
import { normalizeProductPricing } from './pricingNormalization';

export const completeHrManagementPlatform: Product = normalizeProductPricing({
  id: 'complete-hr-management-platform',
  title: 'Complete HR Management Platform',
  subtitle: 'AI-Powered HR, Payroll & Workforce Management Suite',
  description: 'Award-winning all-in-one HR platform combining complete employee management, HMRC-integrated payroll processing, UK pension auto-enrolment, performance tracking, and intelligent recruitment with full UK compliance and GDPR data protection.',
  icon: Users,
  image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  category: 'Human Resources',
  status: 'Live',
  businessTypes: ['#hr-management', '#payroll', '#employee-management', '#benefits', '#enterprise', '#smb', '#performance-management', '#recruitment', '#compliance'],
  features: [
    // Core Employee Management (8 features)
    'Comprehensive Employee Database with UK GDPR Compliance',
    'Employee Self-Service Portal with Mobile Access',
    'Document Management & Digital Signatures',
    'Organisational Chart & Team Structure Visualisation',
    'Employee Onboarding & Offboarding Workflows',
    'Contract Management with UK Employment Law Templates',
    'Employee Records Audit Trail & Version History',
    'Customisable Employee Profiles with Rich Media',

    // Payroll & Benefits Administration (8 features)
    'HMRC-Integrated Payroll Processing & RTI Submissions',
    'UK Pension Auto-Enrolment Compliance',
    'Benefits Administration & Flexible Benefits Portal',
    'Salary Sacrifice Schemes Management',
    'P45/P60/P11D Automated Generation',
    'Statutory Pay Calculations (SSP, SMP, SPP, SAP)',
    'Multi-Currency & International Payroll Support',
    'Payslip Distribution & Employee Pay History',

    // Time & Attendance (7 features)
    'Time Tracking & Timesheet Management',
    'Absence Management with UK Working Time Regulations',
    'Holiday Entitlement Calculation & Carryover',
    'Shift Scheduling & Rota Management',
    'Clock-In/Clock-Out with Geolocation Support',
    'Overtime Tracking & Compliance Monitoring',
    'Bradford Factor & Absence Pattern Analysis',

    // Performance & Development (7 features)
    'Goal Setting & OKR Management Framework',
    'Performance Review Cycles with 360-Degree Feedback',
    'Continuous Feedback & One-to-One Meeting Tools',
    'Competency Framework & Skills Matrix',
    'Learning & Development Tracking',
    'Career Path Planning & Succession Management',
    'Performance Analytics & Team Insights',

    // Recruitment & Onboarding (6 features)
    'Applicant Tracking System (ATS) with Job Board Integration',
    'AI-Powered CV Screening & Candidate Matching',
    'Interview Scheduling & Video Interview Integration',
    'Offer Management & Digital Contract Signing',
    'Pre-Employment Checks & Right to Work Verification',
    'Automated Onboarding Workflows with Task Assignment',

    // Reporting & Analytics (6 features)
    'Real-Time HR Analytics Dashboard',
    'Custom Report Builder with Drag-and-Drop',
    'Workforce Planning & Headcount Forecasting',
    'Diversity & Inclusion Metrics',
    'Turnover Analysis & Retention Insights',
    'Cost Centre Reporting & Budget Tracking',

    // Enterprise Security (8 features)
    'SOC 2 Type II & ISO 27001 Compliance',
    'UK GDPR Data Protection & Privacy Controls',
    'Role-Based Access Control & SSO Integration',
    'Two-Factor Authentication & Biometric Support',
    'Comprehensive Audit Trails & Access Logging',
    'Data Encryption at Rest & in Transit',
    'DSAR Automation & Right to Erasure Support',
    'Enterprise Cloud Infrastructure (AWS UK Regions)',
  ],
  pricing: { setup: 35320, monthly: 1295 },
  color: 'from-blue-600 to-indigo-700',
  demoConfig: {
    type: 'interactive',
    url: '/hr-management/demo',
    title: 'Experience the Complete Platform',
    description: 'Explore our AI-powered HR platform with employee management, payroll processing, and workforce analytics',
    buttonText: 'Launch HR Demo',
    icon: Play,
    buttonColor: 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700',
  },
});

